import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { ColumnDef } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import Dropdown from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/config';
import TableSkeleton from '../../components/TableSkeleton';
import { PlusIcon } from '../../components/IconButton/icons';
import ListPage, { FilterTrigger, SearchInput, StatusSegments, useNarrowViewport } from '../../components/ListPage';
import { useServerFiltering } from '../../utils/useServerFiltering';
import { useUrlFilters, NCR_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import {
  Ncr,
  NcrSummary,
  ncrColumns,
  NCR_TYPE_OPTIONS,
  NCR_STATUS_OPTIONS,
  NCR_SEVERITY_OPTIONS,
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY,
} from './config';
import AddNcrModal from './components/AddNcrModal';
import NcrDetailModal from './components/NcrDetailModal';
import { badge } from './badges';
import './styles.scss';


/** "Nicos Terpkos" -> "NT"; a single word gives its first two letters. */
const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0]?.slice(0, 2).toUpperCase() ?? '';
};

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

const NonConformanceRegister: React.FC = () => {
  const session = useSession();
  const supabase = useSupabaseClient();

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedNcr, setSelectedNcr] = useState<Ncr | null>(null);

  // URL-synced filter state
  const {
    filters: urlFilters,
    setFilter,
    setFilters: setUrlFilters,
    resetFilters: resetUrlFilters,
    hasActiveFilters,
  } = useUrlFilters(NCR_FILTER_DEFINITIONS);

  const searchInput = urlFilters.search;
  const filterStatus = urlFilters.status;
  const filterType = urlFilters.type;
  const filterSeverity = urlFilters.severity;
  const urlPage = urlFilters.page;
  const urlPageSize = urlFilters.pageSize;

  const {
    data: ncrs,
    pagination,
    summary,
    loading,
    filtering,
    error,
    setFilters,
    refetch,
  } = useServerFiltering<Ncr>({
    rpcFunction: 'get_filtered_ncrs',
    defaultFilters: {
      search_term: searchInput || null,
      status_filter: filterStatus !== 'All' ? [filterStatus] : null,
      type_filter: filterType.length > 0 ? filterType : null,
      severity_filter: filterSeverity.length > 0 ? filterSeverity : null,
      project_filter: null,
      page_size: urlPageSize || DEFAULT_PAGE_SIZE,
      page_offset: ((urlPage || 1) - 1) * (urlPageSize || DEFAULT_PAGE_SIZE),
      sort_by: 'reference_seq',
      sort_order: 'desc',
    },
    debounceDelay: SEARCH_DEBOUNCE_DELAY,
  });

  // Sync URL filters -> server filters
  useEffect(() => {
    const pageSize = urlPageSize || DEFAULT_PAGE_SIZE;
    setFilters({
      search_term: searchInput || null,
      status_filter: filterStatus !== 'All' ? [filterStatus] : null,
      type_filter: filterType.length > 0 ? filterType : null,
      severity_filter: filterSeverity.length > 0 ? filterSeverity : null,
      page_size: pageSize,
      page_offset: ((urlPage || 1) - 1) * pageSize,
    });
  }, [searchInput, filterStatus, filterType, filterSeverity, urlPage, urlPageSize, setFilters]);

  // Fetch active team members for the "raised by" dropdown
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!session?.user) return;
      const { data, error: tmError } = await supabase
        .from('active_team_members')
        .select('id, first_name, last_name')
        .order('first_name', { ascending: true });
      if (tmError) {
        console.error('Error fetching team members:', tmError);
      } else {
        setTeamMembers(data || []);
      }
    };
    fetchTeamMembers();
  }, [session, supabase]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ search: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleStatusChange = useCallback((status: string) => {
    setUrlFilters({ status, page: 1 });
  }, [setUrlFilters]);

  const handleClearSearch = useCallback(() => {
    setUrlFilters({ search: '', page: 1 });
  }, [setUrlFilters]);

  const isNarrow = useNarrowViewport();

  const handleTypeChange = useCallback((types: string[]) => {
    setUrlFilters({ type: types, page: 1 });
  }, [setUrlFilters]);

  const handleSeverityChange = useCallback((severities: string[]) => {
    setUrlFilters({ severity: severities, page: 1 });
  }, [setUrlFilters]);

  const handlePageChange = useCallback((page: number) => {
    setFilter('page', page);
  }, [setFilter]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlFilters({ pageSize: newPageSize, page: 1 });
  }, [setUrlFilters]);

  const handleRowClick = useCallback((id: number) => {
    const ncr = ncrs.find(n => n.id === id);
    if (ncr) {
      setSelectedNcr(ncr);
      setIsDetailModalOpen(true);
    }
  }, [ncrs]);

  const handleNcrCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleNcrUpdated = useCallback(() => {
    refetch();
    setIsDetailModalOpen(false);
    setSelectedNcr(null);
  }, [refetch]);

  const handleNcrDeleted = useCallback(() => {
    refetch();
    setIsDetailModalOpen(false);
    setSelectedNcr(null);
  }, [refetch]);

  // Action Status pill-radio options with counts (counts ignore the status filter itself)
  const ncrSummary = summary as NcrSummary | null;
  const statusCountByValue = useMemo<Record<string, number>>(() => ({
    'Open': ncrSummary?.open_count || 0,
    'In Progress': ncrSummary?.in_progress_count || 0,
    'Resolved': ncrSummary?.resolved_count || 0,
    'Closed': ncrSummary?.closed_count || 0,
  }), [ncrSummary]);

  const statusOptions = useMemo(() => [
    { value: 'All', label: 'All', count: ncrSummary?.total_count || 0 },
    ...NCR_STATUS_OPTIONS.map(s => ({ value: s, label: s, count: statusCountByValue[s] })),
  ], [ncrSummary, statusCountByValue]);

  const typeItems: DropdownItem[] = NCR_TYPE_OPTIONS.map(s => ({ id: s, label: s, value: s }));
  const severityItems: DropdownItem[] = NCR_SEVERITY_OPTIONS.map(s => ({ id: s, label: s, value: s }));

  const columns = useMemo<ColumnDef<Ncr>[]>(() => {
    // Column budget (table-layout: fixed; see the Documents register). Ten
    // columns, so the page uses the shell's --tight density; the fixed
    // columns come to ~890px, Project takes 18% and Title the remainder.
    const columnConfig: Record<string, { minWidth?: string; width?: string; maxWidth?: string }> = {
      reference: { minWidth: '96px', width: '96px' },
      type: { minWidth: '96px', width: '96px' },
      date_raised: { minWidth: '112px', width: '112px' },
      title: { minWidth: '180px', width: 'auto' },
      project: { minWidth: '160px', width: '18%' },
      classification: { minWidth: '176px', width: '176px' },
      severity: { minWidth: '100px', width: '100px' },
      status: { minWidth: '116px', width: '116px' },
      target_close_out_date: { minWidth: '112px', width: '112px' },
      raised_by: { minWidth: '124px', width: '124px' }, // the letter-spaced header + the 24px right gutter
    };

    return Object.entries(ncrColumns).map(([key, config]) => ({
      accessorKey: key,
      header: config.header,
      enableSorting: config.sortable ?? false,
      meta: columnConfig[key],
      cell: ({ row }) => {
        const ncr = row.original;
        switch (config.component) {
          case 'TypeBadge':
            return badge(ncr.type);
          case 'StatusBadge':
            return badge(ncr.status);
          case 'SeverityBadge':
            return badge(ncr.severity);
          case 'ClassificationBadge':
            return ncr.classification
              ? <span className="ui-chip" title={ncr.classification}>{ncr.classification}</span>
              : <span className="text-muted">—</span>;
          case 'DateColumn': {
            const dateVal = row.getValue(key) as string | null;
            return <span>{dateVal ? format(parseISO(dateVal), 'd MMM yyyy') : '—'}</span>;
          }
          case 'ProjectColumn':
            return ncr.project ? (
              <span title={`${ncr.project.job_number_display}${ncr.project.description ? ` — ${ncr.project.description}` : ''}`}>
                <strong>{ncr.project.job_number_display}</strong>
                {ncr.project.description ? ` — ${ncr.project.description}` : ''}
              </span>
            ) : <span className="text-muted">—</span>;
          case 'TitleColumn': {
            // Title summarises the NCR; fall back to the detail for legacy rows
            // created before the title field existed.
            const text = ncr.title || ncr.description || ncr.external_reported_details;
            return text
              ? <span className="ncr-desc-cell" title={text}>{text}</span>
              : <span className="text-muted">—</span>;
          }
          case 'RaisedByColumn': {
            if (!ncr.raised_by) return <span className="text-muted">—</span>;
            const name = `${ncr.raised_by.first_name} ${ncr.raised_by.last_name}`;
            return <span className="ui-avatar" title={name} aria-label={name}>{initials(name)}</span>;
          }
          default: {
            const value = row.getValue(key);
            return <span>{(value as string) || '—'}</span>;
          }
        }
      },
    }));
  }, []);

  const currentPage = pagination?.page || urlPage || 1;
  const currentPageSize = pagination?.page_size || urlPageSize || DEFAULT_PAGE_SIZE;

  return (
    <ListPage
      title="Non-Conformance Register"
      className="ncr-page list-page--tight"
      skeleton={{ segmentCount: 5, filterCount: 2, showAction: true }}
      search={
        <SearchInput
          placeholder="Search NCRs, descriptions, projects…"
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          aria-label="Search NCRs"
        />
      }
      actions={
        <button type="button" className="pill-btn pill-btn--solid pill-btn--sm" onClick={() => setIsAddModalOpen(true)}>
          <PlusIcon /> Add NCR
        </button>
      }
      segments={
        <StatusSegments
          aria-label="Action status"
          size={isNarrow ? 'sm' : 'md'}
          value={filterStatus}
          onChange={handleStatusChange}
          segments={statusOptions.map((o) => ({ value: o.value, label: o.label, count: o.count }))}
        />
      }
      filters={
        <>
          <FilterTrigger label="Type" active={filterType.length > 0}>
            <Dropdown items={typeItems} value={filterType} onChange={handleTypeChange} placeholder="All" multiSelect searchable={false} />
          </FilterTrigger>
          <FilterTrigger label="Severity" active={filterSeverity.length > 0}>
            <Dropdown items={severityItems} value={filterSeverity} onChange={handleSeverityChange} placeholder="All" multiSelect searchable={false} />
          </FilterTrigger>
        </>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={resetUrlFilters}
      resultsText={
        !loading && pagination && (
          <>
            Showing {pagination.total_records === 0 ? 0 : ((currentPage - 1) * currentPageSize) + 1}–{Math.min(currentPage * currentPageSize, pagination.total_records)} of {pagination.total_records.toLocaleString()} NCRs
          </>
        )
      }
      footer={
        <Pagination
          variant="bar"
          currentPage={currentPage}
          totalPages={pagination?.total_pages ?? 1}
          totalRecords={pagination?.total_records ?? 0}
          pageSize={currentPageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hasNext={pagination?.has_next ?? false}
          hasPrevious={pagination?.has_previous ?? false}
          loading={loading || filtering}
          showPageInfo
          pageSizeOptions={[25, 50, 100]}
        />
      }
    >
      {error && (
        <div className="list-page__error">
          Error loading NCRs: {typeof error === 'string' ? error : (error as any)?.message || 'Unknown error'}
        </div>
      )}

      {/* Skeleton only on the initial load. During `filtering` the hook keeps
          the previous rows via placeholderData, so they stay on screen, dimmed. */}
      {loading ? (
        <TableSkeleton compact rows={14} columns={10} showHeader />
      ) : ncrs.length === 0 ? (
        <div className="list-page__empty">
          {hasActiveFilters
            ? 'No non-conformances match these filters.'
            : 'No non-conformances yet. Raise your first NCR to get started.'}
        </div>
      ) : (
        <div className={`list-page__body ${filtering ? 'is-filtering' : ''}`.trim()}>
          <Table data={ncrs} columns={columns} onRowClick={(row) => handleRowClick(row.id)} stickyHeader />
        </div>
      )}

      <AddNcrModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onNcrCreated={handleNcrCreated}
        teamMembers={teamMembers}
      />

      <NcrDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedNcr(null);
        }}
        ncr={selectedNcr}
        onNcrUpdated={handleNcrUpdated}
        onNcrDeleted={handleNcrDeleted}
        teamMembers={teamMembers}
      />
    </ListPage>
  );
};

export default NonConformanceRegister;
